import { useMutation } from '@apollo/client';
import {
  useCheckoutCartActions,
  useCheckoutCartState,
} from '../../contexts/CheckoutCartContext';
import { useEffect } from 'react';
import {
  IAddShippingAndBillingAddressesQueryResult,
  IAddressV2,
} from '@monofront/types';
import { ADD_SHIPPING_AND_BILLING_ADDRESS } from '../../queries/checkout';

interface IUseAddShippingAndShippingAddresses {
  addShippingAndBillingAddresses: (
    omsShoppingCartId: string,
    shippingAddress: IAddressV2,
    billingAddress: IAddressV2,
  ) => void;
  loading: boolean;
  error: any;
}

/**
 * This hook is meant to add shipping and billing addresses to a checkout order
 * @returns a hook that is used in the delivery to change the address in the oms cart
 */
export const useAddShippingAndBillingAddresses =
  (): IUseAddShippingAndShippingAddresses => {
    const [addShippingAndBillingAddressesMutation, { loading, error }] =
      useMutation<IAddShippingAndBillingAddressesQueryResult>(
        ADD_SHIPPING_AND_BILLING_ADDRESS,
        {
          context: {
            Headers: {
              'Content-Type': 'application/json; charset=utf-8',
            },
          },
        },
      );
    const { updateIsLoading } = useCheckoutCartActions();
    const { checkoutCart } = useCheckoutCartState();

    const addShippingAndBillingAddresses = (
      omsShoppingCartId: string,
      shippingAddress: IAddressV2,
      billingAddress: IAddressV2,
    ) => {
      const optimisticResponse = checkoutCart
        ? {
            addShippingAndBillingAddresses: {
              ...checkoutCart,
              shippingAddress: {
                ...shippingAddress,
                __typename: 'AddressCartV2',
              },
              billingAddress: {
                ...billingAddress,
                __typename: 'AddressCartV2',
              },
              __typename: 'CartV2',
            },
          }
        : undefined;

      addShippingAndBillingAddressesMutation({
        variables: {
          tokenValue: omsShoppingCartId,
          shippingAddress: {
            firstName: shippingAddress.firstName,
            lastName: shippingAddress.lastName,
            city: shippingAddress.city,
            countryCode: shippingAddress.countryCode,
            postcode: shippingAddress.postcode,
            street: shippingAddress.street,
            company: shippingAddress.company,
            provinceCode: shippingAddress.provinceCode,
            phoneNumber: shippingAddress.phoneNumber,
          },
          billingAddress: {
            firstName: billingAddress.firstName,
            lastName: billingAddress.lastName,
            city: billingAddress.city,
            countryCode: billingAddress.countryCode,
            postcode: billingAddress.postcode,
            street: billingAddress.street,
            company: billingAddress.company,
            provinceCode: billingAddress.provinceCode,
            phoneNumber: billingAddress.phoneNumber,
          },
        },
        optimisticResponse,
      });
    };

    useEffect(() => {
      updateIsLoading(loading);
    }, [loading]);

    return {
      addShippingAndBillingAddresses,
      loading,
      error,
    };
  };

export default useAddShippingAndBillingAddresses;
